"use client";

import { useCallback, useRef } from "react";
import { useSearchParams } from "next/navigation";

// >>> MOW-FILTERS-OKT26 1: adres-opbouw als pure functie (getest in scripts/filters0/pagination.test.mjs)
/**
 * Bouwt het adres met een nieuw paginanummer. Alle andere parameters
 * (utm_*, gclid, fbclid, liveEdit, merk, zoek, ...) blijven byte-voor-byte
 * staan: we knippen alleen `paramName` (en eventuele `dropParams`) uit de
 * ruwe querystring en zetten bij p > 1 `paramName=p` achteraan. Bewust geen
 * URLSearchParams.toString(): dat codeert elke overgebleven waarde opnieuw.
 */
export function bouwPaginaAdres(
  loc: { pathname: string; search: string; hash?: string },
  p: number,
  paramName: string,
  dropParams: string[] = []
): string {
  const weg = new Set<string>([paramName, ...dropParams]);
  const over = loc.search
    .replace(/^\?/, "")
    .split("&")
    .filter(Boolean)
    .filter((deel) => {
      const rauw = deel.split("=")[0];
      let sleutel = rauw;
      try {
        sleutel = decodeURIComponent(rauw.replace(/\+/g, " "));
      } catch {
        // Ongeldige %-reeks: de ruwe sleutel vergelijken, nooit een fout.
      }
      return !weg.has(sleutel);
    });
  if (p > 1) over.push(`${encodeURIComponent(paramName)}=${Math.floor(p)}`);
  return loc.pathname + (over.length > 0 ? `?${over.join("&")}` : "") + (loc.hash || "");
}
// <<< MOW-FILTERS-OKT26 1

/**
 * Paginanummer als single source of truth in de URL (?pagina=N — deep-
 * linkbaar/terug-knop-vriendelijk, i.p.v. lokale state), voor de shop- en
 * categoriepagina's (G10 — vervangt infinite scroll).
 *
 * MOW-FILTERS-OKT26 1: het adres wordt met de native History API bijgewerkt
 * (Next 16 koppelt pushState/replaceState aan useSearchParams), niet meer met
 * router.push/replace: dat was bij elke paginaklik of filterwijziging een
 * serververzoek (op /shop ca. 134 KiB en 278 Firestore-lezingen). De nieuwe
 * URL komt uit window.location op het moment van de klik, niet uit de
 * searchParams van de laatste render: Next verwerkt history.* via een
 * startTransition, dus useSearchParams loopt even achter en een tweede snelle
 * klik zou anders de eerste overschrijven.
 */
export function usePagePagination(paramName: string = "pagina") {
  const searchParams = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get(paramName) || "1", 10) || 1);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const setUrlPage = useCallback(
    (p: number, opts?: { replace?: boolean; dropParams?: string[] }) => {
      const { pathname, search, hash } = window.location;
      const nieuw = bouwPaginaAdres({ pathname, search, hash }, p, paramName, opts?.dropParams);
      // Niets te wijzigen (bv. filterklik terwijl je al op pagina 1 staat): geen
      // historie-ingang en geen router-update.
      if (nieuw === pathname + search + hash) return;
      // history.* pas hier opzoeken: Next vervangt die methoden na het mounten.
      if (opts?.replace) window.history.replaceState(null, "", nieuw);
      else window.history.pushState(null, "", nieuw);
    },
    [paramName]
  );

  // Klik op een paginanummer: URL bijwerken + naar het begin van de
  // resultaten scrollen (niet helemaal naar de paginatop — filters/hero
  // blijven staan, alleen de productkaarten wisselen).
  const goToPage = useCallback(
    (p: number) => {
      setUrlPage(p);
      requestAnimationFrame(() => {
        gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [setUrlPage]
  );

  // Terug naar pagina 1 zonder scroll en zonder de browser-historie te
  // vervuilen (replaceState i.p.v. pushState). MOW-FILTERS-OKT26 1: alleen
  // aanroepen vanuit de handler die de gebruiker aanstuurt (filter, sortering,
  // zoekterm), NIET vanuit een effect: een effect draait ook bij het eerste
  // renderen en wiste daardoor een gedeelde ?pagina=2 (en Terug vanaf een
  // product naar pagina 2). `dropParams` haalt in dezelfde adreswijziging
  // parameters weg (MOW-FILTERS-OKT26 6: ?zoek= na het wissen van de zoekterm).
  const resetPage = useCallback(
    (opts?: { dropParams?: string[] }) => {
      setUrlPage(1, { replace: true, dropParams: opts?.dropParams });
    },
    [setUrlPage]
  );

  return { page, goToPage, resetPage, gridRef };
}
